import * as React from "react";
import { useTheme } from "../../shared/ThemeContext";

interface OnboardingProps {
  onComplete: () => void;
}

interface OnboardingStep {
  title: string;
  descriptionTemplate: string;
  imageSrc: string;
  imageAlt: string;
  imageWidth: number;
  imageHeight: number;
}

const STEPS: OnboardingStep[] = [
  {
    title: "Start from scratch",
    descriptionTemplate:
      "Use {brand} AI to create new presentations from scratch, using preset templates.",
    imageSrc: "assets/v2/onboarding-start.svg",
    imageAlt: "Slides using master onboarding preview",
    imageWidth: 340,
    imageHeight: 180,
  },
  {
    title: "Add or refresh data",
    descriptionTemplate:
      "Add native charts, tables with up to date financial information, matched with your presentation style.",
    imageSrc: "assets/v2/onboarding-refresh.svg",
    imageAlt: "Refresh this graph onboarding preview",
    imageWidth: 340,
    imageHeight: 180,
  },
  {
    title: "New contextual slides",
    descriptionTemplate:
      "Use the {brand} AI to create new slides by retaining context of the entire presentation.",
    imageSrc: "assets/v2/onboarding-context.svg",
    imageAlt: "Add new slide onboarding preview",
    imageWidth: 340,
    imageHeight: 180,
  },
];

export const Onboarding: React.FC<OnboardingProps> = ({ onComplete }) => {
  const { brandName } = useTheme();
  const [currentStepIndex, setCurrentStepIndex] = React.useState(0);

  const step = STEPS[currentStepIndex];
  const isLastStep = currentStepIndex === STEPS.length - 1;
  const description = step.descriptionTemplate.replace("{brand}", brandName);

  const handleNext = () => {
    if (isLastStep) {
      onComplete();
      return;
    }
    setCurrentStepIndex((prev) => Math.min(prev + 1, STEPS.length - 1));
  };

  return (
    <div className="flex min-h-screen flex-col bg-white font-sans text-[#131722]">
      <div className="relative flex flex-1 flex-col overflow-hidden">
        <button
          type="button"
          onClick={onComplete}
          className="absolute right-4 top-4 text-[12px] font-medium text-[#c2c6cc] transition hover:text-[#9ca3ad]"
        >
          Skip
        </button>

        <div className="flex flex-1 flex-col items-center justify-center px-6 text-center">
          <div className="mb-10">
            <img
              src={step.imageSrc}
              alt={step.imageAlt}
              width={step.imageWidth}
              height={step.imageHeight}
              style={{
                shapeRendering: "geometricPrecision",
                textRendering: "geometricPrecision",
              }}
            />
          </div>

          <h2 className="max-w-[300px] text-[24px] font-semibold leading-[1.02] tracking-[-0.02em] text-[#151a24]">
            {step.title}
          </h2>
          <p className="mt-3 max-w-[295px] text-[14px] leading-[1.45] text-[#707783]">
            {description}
          </p>

          <div className="mt-8">
            <button
              type="button"
              onClick={handleNext}
              className="rounded-full bg-[#0076d6] px-6 py-2 text-[14px] font-semibold text-white transition hover:bg-[#0668bb]"
            >
              Next
            </button>
          </div>
        </div>

        <div className="flex items-center justify-center gap-2 pb-4">
          {STEPS.map((_, index) => (
            <span
              key={index}
              className={`h-[4px] rounded-full ${
                index === currentStepIndex ? "w-[28px] bg-[#0076d6]" : "w-[24px] bg-[#d9dde3]"
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default Onboarding;
